import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import { useParams } from "react-router-dom";
import { getPublicSurvey, submitSurveyResponse } from "../../helpers/surveyApi";
import { successMessage, dangerMessage } from "../../components/Toast/Toast";
import Rating from "react-rating";

const PublicSurvey = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState({});
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Dark mode state based on system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Theme configuration
  const theme = {
    bg: isDarkMode ? "#0f172a" : "#f5f7fb",
    cardBg: isDarkMode ? "#1e293b" : "#ffffff",
    text: isDarkMode ? "#f8fafc" : "#111827",
    textMuted: isDarkMode ? "#94a3b8" : "#6b7280",
    border: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(148, 163, 184, 0.35)",
    inputBg: isDarkMode ? "#334155" : "#ffffff",
    inputBorder: isDarkMode ? "#475569" : "#cbd5f5",
    questionBg: isDarkMode ? "rgba(30, 41, 59, 0.5)" : "#f9fafb",
  };

  const fetchSurvey = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getPublicSurvey(surveyId);
      if (result?.status === "success") {
        setSurvey(result.data);
        // Initialize responses object
        const initialResponses = {};
        result.data.questions?.forEach((_, index) => {
          initialResponses[index] = "";
        });
        setResponses(initialResponses);
      } else if (result?.status === "fail" || result?.status === "error") {
        const message = result?.message?.toLowerCase() || "";

        // Backend rule (updated):
        // - If now <= event.endDate: survey accessible when active
        // - If now > event.endDate: 403 "Survey is no longer available. Event has ended."
        if (result?.message?.includes("403") || message.includes("forbidden")) {
          if (message.includes("survey is no longer available. event has ended")) {
            setError("This survey is closed because the event has ended.");
          } else {
            // Generic forbidden state (e.g. inactive survey or other access rules)
            setError("This survey is not available.");
          }
        } else if (result?.message?.includes("404") || message.includes("not found")) {
          setError("Survey not found.");
        } else {
          setError(result?.message || "Failed to load survey");
        }
      }
    } catch (error) {
      setError("Error loading survey. Please try again later.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [surveyId]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const handleResponseChange = (questionIndex, value) => {
    setResponses({
      ...responses,
      [questionIndex]: value,
    });
  };

  const validateForm = () => {
    if (survey?.settings?.requireEmail && !email.trim()) {
      dangerMessage("Email is required");
      return false;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      dangerMessage("Please enter a valid email address");
      return false;
    }

    // Validate required questions
    for (let i = 0; i < survey.questions.length; i++) {
      const question = survey.questions[i];
      if (question.required) {
        const response = responses[i];
        if (!response || (Array.isArray(response) && response.length === 0)) {
          dangerMessage(`Please answer the required question: ${question.question}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const responseData = {
        responses: survey.questions.map((_, index) => ({
          questionId: index.toString(),
          answer: responses[index] || "",
        })),
      };

      if (email) {
        responseData.attendeeEmail = email;
      }

      const result = await submitSurveyResponse(surveyId, responseData);

      if (result?.status === "success") {
        successMessage("Thank you for your feedback!");
        setSubmitted(true);
      } else {
        if (result?.message?.toLowerCase().includes("duplicate")) {
          dangerMessage("You have already submitted a response to this survey.");
        } else {
          dangerMessage(result?.message || "Failed to submit response");
        }
      }
    } catch (error) {
      dangerMessage("Error submitting response. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestionInput = (question, index) => {
    const value = responses[index] || "";

    switch (question.type) {
      case "text":
        return (
          <Input
            type="textarea"
            rows="3"
            value={value}
            onChange={(e) => handleResponseChange(index, e.target.value)}
            placeholder="Enter your answer..."
            required={question.required}
            style={{
              fontSize: 15,
              padding: "12px 14px",
              borderRadius: 10,
              borderColor: theme.inputBorder,
              backgroundColor: theme.inputBg,
              color: theme.text,
              minHeight: 80,
              maxHeight: 200,
              resize: "vertical",
              width: "100%",
              boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.05)"
            }}
          />
        );

      case "rating":
        const maxRating = question.ratingScale || 5;
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
            <Rating
              emptySymbol={<span style={{ fontSize: "2.5rem", color: isDarkMode ? "#334155" : "#e2e8f0", margin: "0 4px", transition: "color 0.2s" }}>★</span>}
              fullSymbol={<span style={{ fontSize: "2.5rem", color: "#fbbf24", margin: "0 4px", textShadow: "0 0 10px rgba(251, 191, 36, 0.4)", transition: "transform 0.2s" }}>★</span>}
              initialRating={value ? parseInt(value) : 0}
              onChange={(newRating) => handleResponseChange(index, newRating.toString())}
              stop={maxRating}
            />
            <p style={{ color: theme.textMuted, fontSize: "13px", margin: 0, fontWeight: 500 }}>
              {value ? `You selected ${value} out of ${maxRating}` : `Select a rating from 1 to ${maxRating}`}
            </p>
          </div>
        );

      case "multipleChoice":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {question.options?.map((option, optIndex) => (
              <label 
                key={optIndex} 
                style={{ 
                  color: theme.text,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: `1px solid ${value === option ? "#f97316" : theme.inputBorder}`,
                  backgroundColor: value === option ? (isDarkMode ? "rgba(249, 115, 22, 0.15)" : "rgba(249, 115, 22, 0.05)") : theme.inputBg,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  margin: 0
                }}
              >
                  <Input
                    type="radio"
                    name={`question_${index}`}
                    value={option}
                    checked={value === option}
                    onChange={(e) => handleResponseChange(index, e.target.value)}
                    required={question.required}
                    style={{ 
                      marginTop: "4px",
                      accentColor: "#f97316",
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      flexShrink: 0
                    }}
                  />
                <span style={{ fontSize: "14px", lineHeight: "1.4" }}>{option}</span>
              </label>
            ))}
          </div>
        );

      case "yesNo":
        return (
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              width: "100%"
            }}
          >
            <button
              type="button"
              onClick={() => handleResponseChange(index, "yes")}
              style={{
                flex: "1",
                padding: "12px 16px",
                borderRadius: "10px",
                border: value === "yes" ? "2px solid #f97316" : `1px solid ${theme.inputBorder}`,
                backgroundColor: value === "yes" ? (isDarkMode ? "rgba(249, 115, 22, 0.15)" : "rgba(249, 115, 22, 0.05)") : theme.inputBg,
                color: value === "yes" ? "#f97316" : theme.text,
                fontSize: 15,
                fontWeight: value === "yes" ? 600 : 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "120px"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                {value === "yes" && <span style={{ fontSize: "16px" }}>✓</span>}
                Yes
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleResponseChange(index, "no")}
              style={{
                flex: "1",
                padding: "12px 16px",
                borderRadius: "10px",
                border: value === "no" ? "2px solid #f97316" : `1px solid ${theme.inputBorder}`,
                backgroundColor: value === "no" ? (isDarkMode ? "rgba(249, 115, 22, 0.15)" : "rgba(249, 115, 22, 0.05)") : theme.inputBg,
                color: value === "no" ? "#f97316" : theme.text,
                fontSize: 15,
                fontWeight: value === "no" ? 600 : 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "120px"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                {value === "no" && <span style={{ fontSize: "16px" }}>✕</span>}
                No
              </div>
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <Alert color="danger">
          <h4>Unable to Load Survey</h4>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="container mt-5">
        <Alert color="warning">
          <h4>Survey Not Found</h4>
          <p>The survey you're looking for doesn't exist or has been removed.</p>
        </Alert>
      </div>
    );
  }

  if (submitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: theme.bg,
          padding: "24px 12px",
        }}
      >
        <div style={{ maxWidth: "820px", width: "100%", margin: "0 auto" }}>
          <Card style={{ borderRadius: 16, border: "none", boxShadow: "0 14px 40px rgba(15, 23, 42, 0.12)", backgroundColor: theme.cardBg }}>
            <CardBody className="text-center p-5">
              <div className="mb-4">
                <div
                  style={{
                    fontSize: "3.5rem",
                    color: "#16a34a",
                    width: 80,
                    height: 80,
                    borderRadius: "999px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    backgroundColor: "rgba(22,163,74,0.08)",
                  }}
                >
                  ✓
                </div>
              </div>
              <h3 style={{ fontWeight: 600, marginBottom: 8, color: theme.text }}>Thank you for your feedback</h3>
              <p style={{ color: theme.textMuted, marginBottom: 0 }}>
                Your response has been recorded. We appreciate you taking the time to help us improve this event.
              </p>
            </CardBody>
          </Card>
          <div
            style={{
              marginTop: 24,
              fontSize: 12,
              color: theme.textMuted,
              textAlign: "center",
            }}
          >
            © {new Date().getFullYear()} Kago Events. All rights reserved.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: theme.bg,
        padding: "0",
        color: theme.text,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ 
        maxWidth: "600px", 
        width: "100%", 
        margin: "0 auto", 
        flex: 1, 
        display: "flex", 
        flexDirection: "column",
        backgroundColor: theme.cardBg,
        boxShadow: "0 0 20px rgba(0,0,0,0.05)"
      }}>
        <Card
          style={{
            borderRadius: 0,
            border: "none",
            backgroundColor: "transparent",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardHeader
            style={{
              borderBottom: "none",
              padding: "32px 24px 24px",
              backgroundColor: "#f97316",
              color: "#ffffff",
              borderBottomLeftRadius: "24px",
              borderBottomRightRadius: "24px",
              marginBottom: "16px",
              boxShadow: "0 4px 12px rgba(249, 115, 22, 0.15)"
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ 
                fontSize: 12, 
                opacity: 0.9, 
                textTransform: "uppercase", 
                letterSpacing: 1.5,
                fontWeight: 600
              }}>
                Feedback Survey
              </div>
              <h2 style={{ fontSize: 28, fontWeight: 700, margin: 0, lineHeight: 1.2 }}>{survey.title}</h2>
              {survey.eventName && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    padding: "6px 12px",
                    borderRadius: 999,
                    backgroundColor: "rgba(0,0,0,0.2)",
                    backdropFilter: "blur(4px)",
                    width: "fit-content",
                    marginTop: 8,
                    fontWeight: 500
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 6,
                      height: 6,
                      borderRadius: "999px",
                      backgroundColor: "#4ade80",
                      boxShadow: "0 0 8px #4ade80"
                    }}
                  />
                  <span>{survey.eventName}</span>
                </div>
              )}
            </div>
          </CardHeader>
          
          <CardBody style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", flex: 1 }}>
            {survey.description && (
              <div style={{ 
                marginBottom: 28, 
                padding: "16px 20px",
                backgroundColor: isDarkMode ? "rgba(51, 65, 85, 0.5)" : "#f8fafc",
                borderRadius: 12,
                borderLeft: "4px solid #f97316"
              }}>
                <p style={{ margin: 0, fontSize: 15, color: theme.text, lineHeight: 1.5 }}>
                  {survey.description}
                </p>
              </div>
            )}

            <Form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              {survey.settings?.requireEmail && (
                <FormGroup className="mb-4">
                  <Label for="email" style={{ fontWeight: 600, fontSize: 15, color: theme.text, marginBottom: 8 }}>
                    Email Address <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your.name@example.com"
                    style={{
                      borderRadius: 10,
                      borderColor: theme.inputBorder,
                      backgroundColor: theme.inputBg,
                      color: theme.text,
                      padding: "12px 16px",
                      fontSize: 15,
                      boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.05)"
                    }}
                  />
                </FormGroup>
              )}

              {survey.questions?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "32px" }}>
                  {survey.questions?.map((question, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.4)" : "#ffffff",
                        borderRadius: 16,
                        padding: "20px",
                        border: `1px solid ${theme.border}`,
                        boxShadow: isDarkMode ? "none" : "0 2px 8px rgba(0,0,0,0.02)",
                      }}
                    >
                      <Label style={{ 
                        fontWeight: 600, 
                        fontSize: 16, 
                        display: "block", 
                        marginBottom: 16, 
                        color: theme.text,
                        lineHeight: 1.4
                      }}>
                        <span style={{ 
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "24px",
                          height: "24px",
                          borderRadius: "12px",
                          backgroundColor: "#f97316",
                          color: "white",
                          fontSize: "13px",
                          marginRight: 10,
                          verticalAlign: "middle"
                        }}>{index + 1}</span>
                        {question.question}
                        {question.required && <span className="text-danger ml-1" style={{ fontSize: "18px" }}>*</span>}
                      </Label>
                      <div>
                        {renderQuestionInput(question, index)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: "auto", paddingTop: "20px" }}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: "#f97316",
                    border: "none",
                    borderRadius: 12,
                    padding: "16px",
                    fontWeight: 600,
                    fontSize: 16,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 8,
                    boxShadow: "0 4px 12px rgba(249, 115, 22, 0.2)",
                    transition: "transform 0.1s ease, box-shadow 0.1s ease"
                  }}
                  onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.98)"}
                  onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" />
                      Processing...
                    </>
                  ) : (
                    "Submit Survey Response"
                  )}
                </Button>
                
                <div style={{ fontSize: 13, color: theme.textMuted, textAlign: "center", marginTop: 16 }}>
                  Fields marked with <span className="text-danger">*</span> are required
                </div>
              </div>
            </Form>
          </CardBody>
        </Card>

        <div
          style={{
            padding: "24px",
            fontSize: 13,
            color: theme.textMuted,
            textAlign: "center",
            borderTop: `1px solid ${theme.border}`,
            backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc"
          }}
        >
          <div style={{ marginBottom: 8 }}>
            Powered by <strong>Kago Events</strong>
          </div>
          <div style={{ opacity: 0.8, fontSize: 12 }}>
            By submitting, you agree to our privacy policy
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicSurvey;
